import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import Plot from 'react-plotly.js';
import Plotly from 'plotly.js-dist-min';
import { AnimatePresence, motion } from 'framer-motion';
import DataEditor, { GridCellKind } from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import { LocateFixedIcon, ChevronDown, Check, Download, FileText, FileSpreadsheet, File, BarChart3, Table2, PanelsTopLeft } from 'lucide-react';

const PROFILE_OPTIONS = [
  { value: 'mri', label: 'MRI', field: 'iri_avg_in_per_mi' },
  { value: 'left', label: 'Left Profile', field: 'iri_left_in_per_mi' },
  { value: 'right', label: 'Right Profile', field: 'iri_right_in_per_mi' },
];

const VIEW_MODES = [
  { value: 'both', label: 'Both', icon: PanelsTopLeft },
  { value: 'chart', label: 'Chart', icon: BarChart3 },
  { value: 'table', label: 'Table', icon: Table2 },
];

// PDF Styling Constants
const PDF_COLORS = {
  black: '#000000',
  darkGray: '#333333',
  gray: '#666666',
  lightGray: '#999999',
  headerBg: '#E8E8E8',
  failBg: '#FFE8E8',
  passBg: '#E8FFE8',
  warnBg: '#FFF8E8',
  borderColor: '#CCCCCC'
};

const PDF_FONT_SIZES = {
  title: 18,
  heading1: 14,
  heading2: 12,
  normal: 10,
  small: 9,
  tiny: 8,
  table: 8
};

const CELL_MARGIN = [4, 3, 4, 3];

// Exception stub bar color
const EXCEPTION_COLOR = '#e5e7eb';
const STUB_HEIGHT_RATIO = 0.05; // 5% of max Y value

// Lazy-load pdfmake
let pdfMakeInstance = null;

async function getPdfMake() {
  if (pdfMakeInstance) return pdfMakeInstance;
  
  const pdfMakeModule = await import('pdfmake/build/pdfmake');
  const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
  
  pdfMakeInstance = pdfMakeModule.default || pdfMakeModule;
  
  const pdfFonts = pdfFontsModule.default || pdfFontsModule;
  if (pdfFonts.pdfMake) {
    pdfMakeInstance.vfs = pdfFonts.pdfMake.vfs;
  } else if (pdfFonts.vfs) {
    pdfMakeInstance.vfs = pdfFonts.vfs;
  }
  
  return pdfMakeInstance;
}

// PDF Cell helpers
function cell(text, options = {}) {
  const cellDef = {
    text: String(text ?? '--'),
    fontSize: PDF_FONT_SIZES.table,
    alignment: options.alignment || 'left',
    bold: options.bold || false,
    color: options.color || PDF_COLORS.black,
    margin: CELL_MARGIN
  };
  
  if (options.fillColor) {
    cellDef.fillColor = options.fillColor;
  }
  
  if (options.colSpan) {
    cellDef.colSpan = options.colSpan;
  }
  
  return cellDef;
}

function headerCell(text, options = {}) {
  return cell(text, {
    ...options,
    bold: true,
    fillColor: PDF_COLORS.headerBg
  });
}

function dataCell(text, options = {}) {
  return cell(text, options);
}

const FixedIntervalChart = ({
  segments,
  isReversed,
  thresholds,
  formatStation,
  getIRIColor,
  colorScale,
  onSegmentSelect,
  session,
  events = []
}) => {
  const [contextMenu, setContextMenu] = useState(null);
  const [xRange, setXRange] = useState(null);
  const [profileType, setProfileType] = useState('mri');
  const [viewMode, setViewMode] = useState('both');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const dropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const hoveredPointRef = useRef(null);
  const plotRef = useRef(null);

  const selectedProfile = PROFILE_OPTIONS.find(p => p.value === profileType);

  useEffect(() => {
    const handleClick = (e) => {
      setContextMenu(null);
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleRelayout = useCallback((event) => {
    let min, max;
    if (event['xaxis.range[0]'] !== undefined) {
      min = event['xaxis.range[0]'];
      max = event['xaxis.range[1]'];
    } else if (event['xaxis.range'] && Array.isArray(event['xaxis.range'])) {
      min = event['xaxis.range'][0];
      max = event['xaxis.range'][1];
    } else if (event['xaxis.autorange'] === true || event.autosize) {
      setXRange(null);
      return;
    }
    if (min !== undefined && max !== undefined) {
      setXRange([parseFloat(min), parseFloat(max)]);
    }
  }, []);

  // Filter ALR events from all events
  const alrEvents = useMemo(() => {
    return events.filter(e =>
      e.event_type === 'LR' &&
      (e.magnitude_in && e.magnitude_in > thresholds.active)
    );
  }, [events, thresholds.active]);

  // Process table data
  const tableData = useMemo(() => {
    const sorted = [...segments]
      .map((seg, index) => {
        const startStation = seg.from_station_ft || 0;
        const endStation = seg.to_station_ft || 0;

        // Match Localized Roughness segment filtering: count thresholded ALRs that overlap the segment.
        const alrCount = alrEvents.filter(alr => {
          const alrStart = alr.from_station_ft || 0;
          const alrStop = alr.to_station_ft || 0;
          const alrPeak = alr.peak_station_ft || 0;

          return (alrStart >= startStation && alrStart <= endStation) ||
                 (alrStop >= startStation && alrStop <= endStation) ||
                 (alrPeak >= startStation && alrPeak <= endStation) ||
                 (alrStart <= startStation && alrStop >= endStation);
        }).length;

        return {
          originalIndex: index,
          startStation,
          endStation,
          segmentWidth: seg.distance_ft || Math.abs(endStation - startStation),
          leftIRI: seg.iri_left_in_per_mi,
          rightIRI: seg.iri_right_in_per_mi,
          mri: seg.iri_avg_in_per_mi,
          isException: seg.exception,
          alrCount
        };
      })
      // Sort by work direction: reversed = descending (high to low), normal = ascending
      .sort((a, b) => isReversed
        ? b.startStation - a.startStation
        : a.startStation - b.startStation);

    // Calculate cumulative distances in work order
    let cumulativeDistance = 0;
    return sorted.map(seg => {
      const startDistance = cumulativeDistance;
      const stopDistance = cumulativeDistance + seg.segmentWidth;
      cumulativeDistance = stopDistance;

      // For reversed jobs, swap start/stop station labels to match work direction
      // Work goes from high station to low station, so "start" is the higher station
      return {
        ...seg,
        startDistance,
        stopDistance,
        startStationLabel: formatStation(isReversed ? seg.endStation : seg.startStation),
        stopStationLabel: formatStation(isReversed ? seg.startStation : seg.endStation)
      };
    });
  }, [segments, formatStation, alrEvents, isReversed]);

  // Valid table data for exports (excludes exceptions)
  const validTableData = useMemo(() => {
    return tableData.filter(row => !row.isException);
  }, [tableData]);

  // Generate chart data for a specific profile type (used for PDF export)
  const generateChartData = useCallback((type) => {
    // First pass: sort by work direction and calculate widths
    const sortedSegments = segments
      .map((seg, index) => {
        const start = seg.from_station_ft || 0;
        const end = seg.to_station_ft || 0;
        // For reversed jobs, work goes high→low, so "work start" is the higher station (end)
        const workStartStation = isReversed ? end : start;
        const workEndStation = isReversed ? start : end;
        return {
          originalIndex: index + 1,
          startStation: start,
          endStation: end,
          width: seg.distance_ft || Math.abs(end - start),
          stationLabel: formatStation(workStartStation),
          endLabel: formatStation(workEndStation),
          leftIRI: seg.exception ? null : (seg.iri_left_in_per_mi || 0),
          rightIRI: seg.exception ? null : (seg.iri_right_in_per_mi || 0),
          mri: seg.exception ? null : (seg.iri_avg_in_per_mi || 0),
          isException: seg.exception
        };
      })
      // Sort by work direction: reversed = descending (high to low), normal = ascending
      .sort((a, b) => isReversed
        ? b.startStation - a.startStation
        : a.startStation - b.startStation);

    // Second pass: calculate cumulative display positions in work order
    let cumulativeDistance = 0;
    const segmentData = sortedSegments.map(seg => {
      const displayStart = cumulativeDistance;
      const displayEnd = cumulativeDistance + seg.width;
      cumulativeDistance = displayEnd;
      return {
        ...seg,
        displayStart,
        displayEnd,
        displayMidpoint: (displayStart + displayEnd) / 2
      };
    });

    // Data is already in work order
    const chartData = segmentData;
    const xValues = chartData.map(item => item.displayMidpoint);
    const widths = chartData.map(item => item.width);

    // Calculate display range from cumulative positions
    const totalDistance = segmentData.length > 0 ? segmentData[segmentData.length - 1].displayEnd : 0;
    const dataMin = 0;
    const dataMax = totalDistance;
    const span = dataMax - dataMin;
    const padding = span > 0 ? span * 0.02 : 100;

    const getYValue = (item) => {
      if (type === 'left') return item.leftIRI;
      if (type === 'right') return item.rightIRI;
      return item.mri;
    };

    // Calculate max Y from valid segments for stub height
    const validYValues = chartData
      .filter(item => !item.isException)
      .map(item => getYValue(item))
      .filter(v => v !== null && v > 0);
    const maxY = validYValues.length > 0 ? Math.max(...validYValues) : 100;
    const stubHeight = maxY * STUB_HEIGHT_RATIO;

    const yVals = chartData.map(item => {
      if (item.isException) return stubHeight;
      return getYValue(item);
    });

    const colors = chartData.map(item => {
      if (item.isException) return EXCEPTION_COLOR;
      const val = getYValue(item);
      return val !== null ? getIRIColor(val) : EXCEPTION_COLOR;
    });

    const profileLabel = PROFILE_OPTIONS.find(p => p.value === type)?.label || 'IRI';

    // Generate tick values using display positions with station labels
    const targetTickCount = 10;
    const step = Math.max(1, Math.ceil(chartData.length / targetTickCount));
    const tickVals = [];
    const tickText = [];
    
    chartData.forEach((item, i) => {
      if (i % step === 0 || i === chartData.length - 1) {
        tickVals.push(item.displayStart);
        tickText.push(item.stationLabel);
      }
    });

    const data = [{
      type: 'bar',
      x: xValues,
      y: yVals,
      width: widths,
      marker: {
        color: colors,
        line: { width: 0.5, color: '#d1d5db' }
      }
    }];

    const layout = {
      title: {
        text: profileLabel,
        font: { size: 12 }
      },
      xaxis: {
        title: { text: isReversed ? 'Station (Reverse Direction)' : 'Station', font: { size: 10 } },
        tickmode: 'array',
        tickvals: tickVals,
        ticktext: tickText,
        tickangle: -45,
        tickfont: { size: 8 },
        type: 'linear',
        // Data is already in work order, so axis always goes 0 to max
        range: [dataMin - padding, dataMax + padding]
      },
      yaxis: {
        title: { text: `${profileLabel} (in/mi)`, font: { size: 10 } },
        rangemode: 'tozero',
        tickfont: { size: 9 }
      },
      shapes: [{
        type: 'line',
        x0: dataMin - padding,
        x1: dataMax + padding,
        y0: thresholds.active,
        y1: thresholds.active,
        line: { color: 'rgb(220, 38, 38)', width: 2, dash: 'dash' }
      }],
      bargap: 0,
      margin: { t: 30, r: 20, b: 70, l: 45 },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white'
    };

    return { data, layout };
  }, [segments, isReversed, thresholds.active, getIRIColor, formatStation]);

  // Export functions - now using validTableData (excludes exceptions)
  const exportToCSV = useCallback(() => {
    const headers = ['Start Distance (ft)', 'Stop Distance (ft)', 'Length (ft)', 'Start Station', 'Stop Station', 'Left IRI (in/mi)', 'Right IRI (in/mi)', 'MRI (in/mi)', 'ALR Count'];
    const rows = validTableData.map(row => [
      row.startDistance.toFixed(1),
      row.stopDistance.toFixed(1),
      row.segmentWidth.toFixed(1),
      row.startStationLabel,
      row.stopStationLabel,
      row.leftIRI?.toFixed(1) ?? '',
      row.rightIRI?.toFixed(1) ?? '',
      row.mri?.toFixed(1) ?? '',
      row.alrCount
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'segment_smoothness_data.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  }, [validTableData]);

  const exportToTXT = useCallback(() => {
    const headers = ['Start Dist (ft)', 'Stop Dist (ft)', 'Length (ft)', 'Start Station', 'Stop Station', 'Left IRI', 'Right IRI', 'MRI', 'ALRs'];
    const colWidths = [16, 16, 12, 14, 14, 12, 12, 12, 8];

    const padCell = (str, width) => str.toString().padEnd(width);
    const headerLine = headers.map((h, i) => padCell(h, colWidths[i])).join('');
    const separatorLine = colWidths.map(w => '-'.repeat(w)).join('');

    const rows = validTableData.map(row => [
      padCell(row.startDistance.toFixed(1), colWidths[0]),
      padCell(row.stopDistance.toFixed(1), colWidths[1]),
      padCell(row.segmentWidth.toFixed(1), colWidths[2]),
      padCell(row.startStationLabel, colWidths[3]),
      padCell(row.stopStationLabel, colWidths[4]),
      padCell(row.leftIRI?.toFixed(1) ?? '-', colWidths[5]),
      padCell(row.rightIRI?.toFixed(1) ?? '-', colWidths[6]),
      padCell(row.mri?.toFixed(1) ?? '-', colWidths[7]),
      padCell(row.alrCount.toString(), colWidths[8])
    ].join(''));

    const exceptionCount = tableData.filter(r => r.isException).length;

    const txtContent = [
      'SEGMENT SMOOTHNESS REPORT',
      '='.repeat(104),
      '',
      `Generated: ${new Date().toLocaleString()}`,
      `Total Segments: ${validTableData.length}`,
      `Exception Segments (excluded): ${exceptionCount}`,
      `Threshold: ${thresholds.active} in/mi`,
      '',
      headerLine,
      separatorLine,
      ...rows
    ].join('\n');

    const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'segment_smoothness_data.txt';
    link.click();
    URL.revokeObjectURL(link.href);
  }, [validTableData, tableData, thresholds.active]);

  const exportToPDF = useCallback(async () => {
    setIsExporting(true);
    try {
      const pdfMake = await getPdfMake();

      // Generate chart images - higher resolution with scale factor
      const chartTypes = ['mri', 'left', 'right'];
      const chartImages = [];

      for (const type of chartTypes) {
        const { data, layout } = generateChartData(type);
        const tempDiv = document.createElement('div');
        tempDiv.style.width = '1200px';
        tempDiv.style.height = '270px';
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        document.body.appendChild(tempDiv);

        await Plotly.newPlot(tempDiv, data, { ...layout, width: 1200, height: 270 });
        const imgData = await Plotly.toImage(tempDiv, { format: 'png', width: 1200, height: 270, scale: 2 });
        chartImages.push({ type, imgData, label: PROFILE_OPTIONS.find(p => p.value === type)?.label });

        Plotly.purge(tempDiv);
        document.body.removeChild(tempDiv);
      }

      // Calculate summary stats from valid segments only
      const avgMRI = validTableData.length > 0 
        ? (validTableData.reduce((sum, r) => sum + (r.mri || 0), 0) / validTableData.length).toFixed(1)
        : '--';
      const avgLeft = validTableData.length > 0 
        ? (validTableData.reduce((sum, r) => sum + (r.leftIRI || 0), 0) / validTableData.length).toFixed(1)
        : '--';
      const avgRight = validTableData.length > 0 
        ? (validTableData.reduce((sum, r) => sum + (r.rightIRI || 0), 0) / validTableData.length).toFixed(1)
        : '--';
      const exceedingThreshold = validTableData.filter(r => (r.mri || 0) > thresholds.active).length;
      const exceptionCount = tableData.filter(r => r.isException).length;

      // Session metadata
      const sessionName = session?.name || session?.filename || 'Untitled Session';
      const operator = session?.operator || session?.meta?.operator || '--';
      const runDate = session?.run_date 
        ? new Date(session.run_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : '--';

      // Build table body - using validTableData (excludes exceptions)
      const tableHeaders = [
        headerCell('Start Dist (ft)'),
        headerCell('Stop Dist (ft)'),
        headerCell('Length (ft)', { alignment: 'right' }),
        headerCell('Start Station'),
        headerCell('Stop Station'),
        headerCell('Left IRI', { alignment: 'right' }),
        headerCell('Right IRI', { alignment: 'right' }),
        headerCell('MRI', { alignment: 'right' }),
        headerCell('ALRs', { alignment: 'right' })
      ];

      const tableRows = validTableData.map(row => {
        const exceedsThreshold = row.mri !== null && row.mri > thresholds.active;
        const rowFillColor = exceedsThreshold ? PDF_COLORS.failBg : undefined;
        
        return [
          dataCell(row.startDistance.toFixed(1), { fillColor: rowFillColor }),
          dataCell(row.stopDistance.toFixed(1), { fillColor: rowFillColor }),
          dataCell(row.segmentWidth.toFixed(1), { alignment: 'right', fillColor: rowFillColor }),
          dataCell(row.startStationLabel, { fillColor: rowFillColor }),
          dataCell(row.stopStationLabel, { fillColor: rowFillColor }),
          dataCell(row.leftIRI?.toFixed(1) ?? '--', { alignment: 'right', fillColor: rowFillColor }),
          dataCell(row.rightIRI?.toFixed(1) ?? '--', { alignment: 'right', fillColor: rowFillColor }),
          dataCell(row.mri?.toFixed(1) ?? '--', { alignment: 'right', fillColor: rowFillColor }),
          dataCell(row.alrCount.toString(), { alignment: 'right', fillColor: rowFillColor })
        ];
      });

      // Custom table layout with outside border
      const borderedTableLayout = {
        hLineWidth: function(i, node) {
          if (i === 0 || i === 1 || i === node.table.body.length) return 1;
          return 0;
        },
        vLineWidth: function(i, node) {
          if (i === 0 || i === node.table.widths.length) return 1;
          return 0;
        },
        hLineColor: function() { return PDF_COLORS.borderColor; },
        vLineColor: function() { return PDF_COLORS.borderColor; }
      };

      // Segment data table layout - outside border with light horizontal lines
      const segmentTableLayout = {
        hLineWidth: function(i, node) {
          if (i === 0 || i === 1 || i === node.table.body.length) return 1;
          return 0.5;
        },
        vLineWidth: function(i, node) {
          if (i === 0 || i === node.table.widths.length) return 1;
          return 0;
        },
        hLineColor: function(i, node) {
          if (i === 0 || i === 1 || i === node.table.body.length) return PDF_COLORS.borderColor;
          return '#E5E5E5';
        },
        vLineColor: function() { return PDF_COLORS.borderColor; }
      };

      // Build legend items
      const legendItems = colorScale.ranges.map((range, index) => {
        const prevMax = index === 0 ? 0 : colorScale.ranges[index - 1].max;
        const isLast = index === colorScale.ranges.length - 1;
        const label = range.description || (isLast
          ? `> ${prevMax} in/mi (${range.label})`
          : `${prevMax}-${range.max} in/mi (${range.label})`);
        return { color: range.color, label };
      });

      const content = [
        // Title - left aligned
        {
          text: 'SEGMENT SMOOTHNESS REPORT',
          style: 'title',
          alignment: 'left',
          margin: [0, 0, 0, 8]
        },

        // Session metadata
        {
          columns: [
            {
              width: '50%',
              stack: [
                { text: [{ text: 'Session: ', bold: true }, sessionName], fontSize: 9 },
                { text: [{ text: 'Operator: ', bold: true }, operator], fontSize: 9 }
              ]
            },
            {
              width: '50%',
              stack: [
                { text: [{ text: 'Run Date: ', bold: true }, runDate], fontSize: 9, alignment: 'right' }
              ]
            }
          ],
          margin: [0, 0, 0, 10]
        },

        // Summary Statistics - all in one row with border
        {
          table: {
            widths: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto', '*'],
            body: [
              [
                headerCell('Segments'),
                headerCell('Exceptions'),
                headerCell('Avg MRI'),
                headerCell('Avg Left'),
                headerCell('Avg Right'),
                headerCell('Threshold'),
                headerCell('Exceeding')
              ],
              [
                dataCell(validTableData.length.toString(), { alignment: 'center' }),
                dataCell(exceptionCount.toString(), { alignment: 'center' }),
                dataCell(`${avgMRI} in/mi`, { alignment: 'center' }),
                dataCell(`${avgLeft} in/mi`, { alignment: 'center' }),
                dataCell(`${avgRight} in/mi`, { alignment: 'center' }),
                dataCell(`${thresholds.active} in/mi`, { alignment: 'center' }),
                dataCell(`${exceedingThreshold} (${validTableData.length > 0 ? ((exceedingThreshold / validTableData.length) * 100).toFixed(1) : 0}%)`, { 
                  alignment: 'center',
                  fillColor: exceedingThreshold > 0 ? PDF_COLORS.warnBg : PDF_COLORS.passBg
                })
              ]
            ]
          },
          layout: borderedTableLayout,
          margin: [0, 0, 0, 8]
        },

        // MRI Chart
        {
          image: chartImages[0].imgData,
          width: 535,
          alignment: 'center',
          margin: [0, 5, 0, 12]
        },

        // Left IRI Chart
        {
          image: chartImages[1].imgData,
          width: 535,
          alignment: 'center',
          margin: [0, 5, 0, 12]
        },

        // Right IRI Chart
        {
          image: chartImages[2].imgData,
          width: 535,
          alignment: 'center',
          margin: [0, 5, 0, 8]
        },

        // Legend
        {
          columns: legendItems.map(item => ({
            width: 'auto',
            stack: [
              {
                canvas: [{
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: 10,
                  h: 10,
                  color: item.color
                }]
              }
            ],
            margin: [0, 0, 2, 0]
          })).flatMap((colorBox, index) => [
            colorBox,
            {
              text: legendItems[index].label,
              fontSize: 7,
              margin: [0, 1, 10, 0]
            }
          ]),
          margin: [0, 0, 0, 10]
        },

        // Data Table
        {
          text: 'SEGMENT DATA',
          style: 'heading1',
          pageBreak: 'before',
          margin: [0, 0, 0, 6]
        },
        {
          text: `${validTableData.length} segments shown (${exceptionCount} exception segments excluded)`,
          fontSize: 9,
          color: PDF_COLORS.gray,
          margin: [0, 0, 0, 10]
        },
        {
          table: {
            headerRows: 1,
            widths: ['11%', '11%', '10%', '12%', '12%', '11%', '11%', '11%', '11%'],
            body: [tableHeaders, ...tableRows]
          },
          layout: segmentTableLayout
        }
      ];

      const docDefinition = {
        pageSize: 'LETTER',
        pageOrientation: 'portrait',
        pageMargins: [30, 30, 30, 50],
        
        defaultStyle: {
          fontSize: PDF_FONT_SIZES.normal
        },
        
        styles: {
          title: {
            fontSize: PDF_FONT_SIZES.title,
            bold: true,
            color: PDF_COLORS.darkGray
          },
          heading1: {
            fontSize: PDF_FONT_SIZES.heading1,
            bold: true,
            color: PDF_COLORS.darkGray
          },
          heading2: {
            fontSize: PDF_FONT_SIZES.heading2,
            bold: true,
            color: PDF_COLORS.darkGray
          },
          normal: {
            fontSize: PDF_FONT_SIZES.normal
          },
          small: {
            fontSize: PDF_FONT_SIZES.small
          }
        },
        
        footer: function(currentPage, pageCount) {
          return {
            columns: [
              { 
                text: `Generated: ${new Date().toLocaleString()}`,
                alignment: 'left',
                fontSize: PDF_FONT_SIZES.tiny,
                color: PDF_COLORS.gray,
                margin: [30, 0, 0, 0]
              },
              { 
                text: `Page ${currentPage} of ${pageCount}`,
                alignment: 'right',
                fontSize: PDF_FONT_SIZES.tiny,
                color: PDF_COLORS.gray,
                margin: [0, 0, 30, 0]
              }
            ],
            margin: [0, 15, 0, 0]
          };
        },
        
        content
      };

      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      pdfDocGenerator.download('segment_smoothness_report.pdf');

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [validTableData, tableData, generateChartData, thresholds.active, session, colorScale]);

  const { plotData, layout } = useMemo(() => {
    // First pass: sort by work direction and calculate segment widths
    const sortedSegments = segments
      .map((seg, index) => {
        const start = seg.from_station_ft || 0;
        const end = seg.to_station_ft || 0;
        // For reversed jobs, work goes high→low, so "work start" is the higher station (end)
        const workStartStation = isReversed ? end : start;
        const workEndStation = isReversed ? start : end;
        return {
          originalIndex: index + 1,
          startStation: start,
          endStation: end,
          width: seg.distance_ft || Math.abs(end - start),
          stationLabel: formatStation(workStartStation),
          endLabel: formatStation(workEndStation),
          leftIRI: seg.exception ? null : (seg.iri_left_in_per_mi || 0),
          rightIRI: seg.exception ? null : (seg.iri_right_in_per_mi || 0),
          mri: seg.exception ? null : (seg.iri_avg_in_per_mi || 0),
          isException: seg.exception
        };
      })
      // Sort by work direction: reversed = descending (high to low), normal = ascending
      .sort((a, b) => isReversed
        ? b.startStation - a.startStation
        : a.startStation - b.startStation);

    // Second pass: calculate cumulative display positions in work order
    let cumulativeDistance = 0;
    const segmentData = sortedSegments.map(seg => {
      const displayStart = cumulativeDistance;
      const displayEnd = cumulativeDistance + seg.width;
      cumulativeDistance = displayEnd;
      return {
        ...seg,
        displayStart,
        displayEnd,
        displayMidpoint: (displayStart + displayEnd) / 2
      };
    });

    // Data is already in work order, no need to reverse
    const chartData = segmentData;

    // Use display positions for X values (cumulative distance based)
    const xValues = chartData.map(item => item.displayMidpoint);
    const widths = chartData.map(item => item.width);

    const customData = chartData.map(item => [
      item.stationLabel,
      item.endLabel,
      item.originalIndex,
      item.leftIRI,
      item.rightIRI,
      item.isException
    ]);

    // Calculate display range from cumulative positions
    const totalDistance = segmentData.length > 0 ? segmentData[segmentData.length - 1].displayEnd : 0;
    const dataMin = 0;
    const dataMax = totalDistance;
    const span = dataMax - dataMin;
    const padding = span > 0 ? span * 0.02 : 100;

    // Calculate visible range for tick calculation
    const currentMin = xRange ? Math.min(xRange[0], xRange[1]) : dataMin;
    const currentMax = xRange ? Math.max(xRange[0], xRange[1]) : dataMax;

    const visibleItems = chartData.filter(d => d.displayMidpoint >= currentMin && d.displayMidpoint <= currentMax);
    const targetLabelCount = 15;
    const step = Math.max(1, Math.ceil(visibleItems.length / targetLabelCount));

    // Use display positions for ticks but show station labels
    const tickVals = [];
    const tickText = [];

    visibleItems.forEach((item, i) => {
      if (i % step === 0) {
        tickVals.push(item.displayStart);
        tickText.push(item.stationLabel);
      }
    });

    const getYValue = (item) => {
      if (profileType === 'left') return item.leftIRI;
      if (profileType === 'right') return item.rightIRI;
      return item.mri;
    };

    // Calculate max Y from valid segments for stub height
    const validYValues = chartData
      .filter(item => !item.isException)
      .map(item => getYValue(item))
      .filter(v => v !== null && v > 0);
    const maxY = validYValues.length > 0 ? Math.max(...validYValues) : 100;
    const stubHeight = maxY * STUB_HEIGHT_RATIO;
    
    const yVals = chartData.map(item => {
      if (item.isException) return stubHeight;
      return getYValue(item);
    });

    const colors = chartData.map(item => {
      if (item.isException) return EXCEPTION_COLOR;
      const val = getYValue(item);
      return val !== null ? getIRIColor(val) : EXCEPTION_COLOR;
    });

    const _plotData = [{
      type: 'bar',
      x: xValues,
      y: yVals,
      width: widths,
      marker: {
        color: colors,
        line: { width: 0.5, color: '#d1d5db' }
      },
      hovertemplate: chartData.map((item, i) => 
        item.isException 
          ? '<b>Exception Segment</b><br>Start: %{customdata[0]}<br>End: %{customdata[1]}<extra></extra>'
          : `<b>${selectedProfile.label}:</b> %{y:.1f} in/mi<br>Start: %{customdata[0]}<br>End: %{customdata[1]}<extra></extra>`
      ),
      customdata: customData,
    }];

    const _layout = {
      xaxis: {
        title: isReversed ? 'Station (Reverse Direction) →' : 'Station →',
        tickmode: 'array',
        tickvals: tickVals,
        ticktext: tickText,
        tickangle: -45,
        type: 'linear',
        // Data is already in work order, so axis always goes 0 to max (left to right)
        range: xRange ? xRange : [dataMin - padding, dataMax + padding]
      },
      yaxis: {
        title: `${selectedProfile.label} (in/mi)`,
        rangemode: 'tozero',
        nticks: 20
      },
      hoverlabel: { align: 'left', namelength: -1 },
      hovermode: 'closest',
      shapes: [{
        type: 'line',
        x0: dataMin - padding,
        x1: dataMax + padding,
        y0: thresholds.active,
        y1: thresholds.active,
        line: { color: 'rgb(220, 38, 38)', width: 2, dash: 'dash' }
      }],
      barmode: 'overlay',
      bargap: 0,
      bargroupgap: 0,
      margin: { t: 50, r: 50, b: 80, l: 60 },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      autosize: true,
      showlegend: false
    };

    return { plotData: _plotData, layout: _layout };
  }, [segments, isReversed, thresholds.active, getIRIColor, formatStation, xRange, profileType, selectedProfile]);

  const config = {
    responsive: true,
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['lasso2d', 'select2d']
  };

  const exceptionCount = segments.filter(item => item.exception).length;
  const validCount = segments.filter(item => !item.exception).length;
  const showChart = viewMode === 'both' || viewMode === 'chart';
  const showTable = viewMode === 'both' || viewMode === 'table';

  useEffect(() => {
    if (!showChart) {
      setDropdownOpen(false);
      setContextMenu(null);
      hoveredPointRef.current = null;
    }
  }, [showChart]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (hoveredPointRef.current && !hoveredPointRef.current.customdata?.[5]) {
      // Only show context menu for non-exception segments
      const segmentNum = hoveredPointRef.current.customdata[2];
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        segmentNumber: segmentNum
      });
    }
  };

  const formatIRI = (value) => {
    if (value === null || value === undefined) return '—';
    return value.toFixed(1);
  };

  const formatDistance = (value) => {
    return value.toFixed(1);
  };

  const segmentGridColumns = useMemo(() => {
    const dataColumns = [
      { id: 'startStation', title: 'Start Station', width: 135 },
      { id: 'stopStation', title: 'Stop Station', width: 135 },
      { id: 'startDistance', title: 'Start Ft', width: 110 },
      { id: 'stopDistance', title: 'Stop Ft', width: 110 },
      { id: 'length', title: 'Length', width: 100 },
      { id: 'leftIRI', title: 'Left IRI', width: 110 },
      { id: 'rightIRI', title: 'Right IRI', width: 110 },
      { id: 'mri', title: 'MRI', width: 100 },
      { id: 'alrs', title: 'ALRs', width: 90 },
    ];
    const fillerColumns = Array.from({ length: 8 }, (_, index) => ({
      id: `blank-${index + 1}`,
      title: '',
      width: 110,
      grow: 1,
    }));

    return [...dataColumns, ...fillerColumns];
  }, []);

  const segmentGridRows = useMemo(() => validTableData.map(row => ({
    segmentNumber: row.originalIndex + 1,
    exceedsThreshold: row.mri !== null && row.mri > thresholds.active,
    startDistance: formatDistance(row.startDistance),
    stopDistance: formatDistance(row.stopDistance),
    length: formatDistance(row.segmentWidth),
    startStation: row.startStationLabel,
    stopStation: row.stopStationLabel,
    leftIRI: formatIRI(row.leftIRI),
    rightIRI: formatIRI(row.rightIRI),
    mri: formatIRI(row.mri),
    alrs: String(row.alrCount || 0),
  })), [validTableData, thresholds.active]);

  const getSegmentCellContent = useCallback(([col, row]) => {
    const gridRow = segmentGridRows[row];
    const column = segmentGridColumns[col];
    const value = gridRow?.[column?.id] ?? '';

    return {
      kind: GridCellKind.Text,
      data: value,
      displayData: value,
      allowOverlay: false,
      readonly: true,
      themeOverride: gridRow?.exceedsThreshold ? {
        bgCell: '#fee2e2',
        bgCellMedium: '#fecaca',
      } : undefined,
    };
  }, [segmentGridColumns, segmentGridRows]);

  const handleSegmentCellActivated = useCallback(([, row]) => {
    const gridRow = segmentGridRows[row];
    if (gridRow) {
      onSegmentSelect(gridRow.segmentNumber);
    }
  }, [onSegmentSelect, segmentGridRows]);

  return (
    <div className="bg-white rounded-sm shadow-sm relative">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 h-20">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Segment Smoothness
              {isReversed && (
                <span className="text-sm font-normal text-blue-600 ml-2">(Reverse Direction)</span>
              )}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {validCount} valid segments, {exceptionCount} exception segments
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="inline-flex h-9 items-center rounded border border-gray-300 bg-white p-0.5">
              {VIEW_MODES.map(option => {
                const Icon = option.icon;
                const isActive = viewMode === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setViewMode(option.value)}
                    className={`h-7 px-2.5 text-xs font-medium rounded flex items-center gap-1.5 transition-colors ${
                      isActive
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {option.label}
                  </button>
                );
              })}
            </div>

            {/* Export Dropdown */}
            <div className="relative" ref={exportDropdownRef}>
              <button
                type="button"
                onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                disabled={isExporting}
                className="relative w-32 bg-white border border-gray-300 rounded px-3 py-1.5 text-left text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  {isExporting ? 'Exporting...' : 'Export'}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${exportDropdownOpen ? 'rotate-180' : ''}`} />
                </span>
              </button>

              {exportDropdownOpen && !isExporting && (
                <div className="absolute z-10 mt-1 w-48 bg-white border border-gray-200 rounded shadow-lg right-0">
                  <ul className="py-1">
                    <li>
                      <button
                        type="button"
                        onClick={() => {
                          exportToPDF();
                          setExportDropdownOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4 text-red-600" />
                        <div>
                          <div className="font-medium">PDF Report</div>
                          <div className="text-xs text-gray-500">Charts & data table</div>
                        </div>
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={() => {
                          exportToCSV();
                          setExportDropdownOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <FileSpreadsheet className="w-4 h-4 text-green-600" />
                        <div>
                          <div className="font-medium">CSV</div>
                          <div className="text-xs text-gray-500">Spreadsheet format</div>
                        </div>
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={() => {
                          exportToTXT();
                          setExportDropdownOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <File className="w-4 h-4 text-gray-600" />
                        <div>
                          <div className="font-medium">Text File</div>
                          <div className="text-xs text-gray-500">Plain text format</div>
                        </div>
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Profile Type Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => showChart && setDropdownOpen(!dropdownOpen)}
                disabled={!showChart}
                className="relative w-40 bg-white border border-gray-300 rounded px-3 py-1.5 text-left text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300"
              >
                <span className="block truncate">{selectedProfile.label}</span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </span>
              </button>

              {dropdownOpen && showChart && (
                <div className="absolute z-10 mt-1 w-40 bg-white border border-gray-200 rounded shadow-lg">
                  <ul className="py-1">
                    {PROFILE_OPTIONS.map(option => (
                      <li key={option.value}>
                        <button
                          type="button"
                          onClick={() => {
                            setProfileType(option.value);
                            setDropdownOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
                        >
                          <span>{option.label}</span>
                          {profileType === option.value && (
                            <Check className="w-4 h-4 text-blue-600" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden">
        <AnimatePresence initial={false}>
          {showChart && (
            <motion.section
              key="chart"
              layout
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="px-6 pt-6 pb-6"
              onContextMenu={handleContextMenu}
            >
              <div style={{ height: '400px', width: '100%' }}>
                <Plot
                  ref={plotRef}
                  data={plotData}
                  layout={layout}
                  config={config}
                  style={{ width: '100%', height: '100%' }}
                  useResizeHandler={true}
                  onRelayout={handleRelayout}
                  onHover={(data) => {
                    if (data.points && data.points.length > 0) {
                      hoveredPointRef.current = data.points[0];
                    }
                  }}
                  onUnhover={() => { hoveredPointRef.current = null; }}
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-4 text-xs">
                {colorScale.ranges.map((range, index) => {
                  const prevMax = index === 0 ? 0 : colorScale.ranges[index - 1].max;
                  const isLast = index === colorScale.ranges.length - 1;
                  const displayText = range.description || (isLast
                    ? `> ${prevMax} (${range.label})`
                    : `${prevMax}-${range.max} (${range.label})`);

                  return (
                    <div key={index} className="flex items-center">
                      <div className="w-3 h-3 mr-2" style={{ backgroundColor: range.color }}></div>
                      <span>{displayText}</span>
                    </div>
                  );
                })}
                {exceptionCount > 0 && (
                  <div className="flex items-center">
                    <div className="w-3 h-3 mr-2" style={{ backgroundColor: EXCEPTION_COLOR }}></div>
                    <span>Exception (No Data)</span>
                  </div>
                )}
              </div>
            </motion.section>
          )}

          {showTable && (
            <motion.section
              key="table"
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={`${showChart ? 'border-t border-gray-200' : ''}`}
            >
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-700">Segment Data</h4>
                <span className="text-xs text-gray-500">
                  {validTableData.length} segments ({exceptionCount} exceptions hidden)
                </span>
              </div>
              <div className={viewMode === 'table' ? 'h-[calc(100vh-220px)] min-h-[520px]' : 'h-80'}>
                <DataEditor
                  columns={segmentGridColumns}
                  rows={segmentGridRows.length}
                  getCellContent={getSegmentCellContent}
                  getCellsForSelection={true}
                  rowMarkers="number"
                  rowHeight={34}
                  headerHeight={38}
                  smoothScrollX={true}
                  smoothScrollY={true}
                  rangeSelect="multi-rect"
                  freezeColumns={1}
                  onCellActivated={handleSegmentCellActivated}
                  theme={{
                    accentColor: '#2563eb',
                    accentLight: 'rgba(37, 99, 235, 0.12)',
                    fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
                    cellHorizontalPadding: 12,
                    headerFontStyle: '600 12px',
                    baseFontStyle: '13px',
                  }}
                />
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>

      {contextMenu && (
        <div
          className="fixed z-50 bg-white border border-gray-200 shadow-lg rounded-sm py-1 min-w-[180px] animate-in fade-in zoom-in-95 duration-100"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="w-full text-left px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-600 flex items-center gap-2 transition-colors"
            onClick={() => {
              onSegmentSelect(contextMenu.segmentNumber);
              setContextMenu(null);
            }}
          >
            <LocateFixedIcon className="w-4 h-4" />
            View ALRs in Segment {contextMenu.segmentNumber}
          </button>
        </div>
      )}
    </div>
  );
};

export default FixedIntervalChart;
